import type { GameState, Card, SeatIndex } from "./protocol";

const SUIT_ORDER: Record<string, number> = { oros: 0, copas: 1, bastos: 2, espadas: 3 };

function plainValue(s: string, r: number): number {
  const isBlack = s === "espadas" || s === "bastos";
  const map: Record<number, number> = isBlack
    ? { 12: 10, 11: 9, 10: 8, 7: 7, 6: 6, 5: 5, 4: 4, 3: 3, 2: 2, 1: 1 }
    : { 12: 10, 11: 9, 10: 8, 1: 7, 2: 6, 3: 5, 4: 4, 5: 3, 6: 2, 7: 1 };
  return map[r] || 0;
}

function sortHand(hand: Card[]): Card[] {
  const matadors: Card[] = [];
  const rest: Card[] = [];
  for (const c of hand) {
    if ((c.s === "espadas" && c.r === 1) || (c.s === "bastos" && c.r === 1)) {
      matadors.push(c);
    } else {
      rest.push(c);
    }
  }
  matadors.sort((a, b) => (a.s === "espadas" ? -1 : 1));
  rest.sort((a, b) => {
    const suitDiff = SUIT_ORDER[a.s] - SUIT_ORDER[b.s];
    if (suitDiff !== 0) return suitDiff;
    return plainValue(b.s, b.r) - plainValue(a.s, a.r);
  });
  return [...matadors, ...rest];
}

export type StateListener = (state: ClientState) => void;

export class ClientState {
  game: GameState | null = null;
  hand: Card[] = [];
  mySeat: SeatIndex | null = null;
  selectedCards = new Set<string>();
  roomCode: string | null = null;
  private listeners = new Set<StateListener>();

  private activeSeats(): SeatIndex[] {
    const game = this.game;
    if (!game) return [0, 1, 2];
    if (game.contract === "penetro") return [0, 1, 2, 3];
    if (game.mode === "tresillo") return [0, 1, 2];
    return ([0, 1, 2, 3] as SeatIndex[]).filter((seat) => seat !== game.resting);
  }

  update(gameState: GameState, hand: Card[] | null): void {
    const turnPlayer =
      gameState.turn !== null ? gameState.players[gameState.turn] : undefined;
    const shouldHideTurnDeadline =
      typeof gameState.turnDeadline === "number" &&
      turnPlayer !== undefined &&
      !turnPlayer.isBot &&
      turnPlayer.connected;

    this.game = shouldHideTurnDeadline
      ? { ...gameState, turnDeadline: undefined }
      : gameState;
    this.hand = sortHand(hand ?? []);
    if (this.selectedCards.size > 0) {
      const validIds = new Set(this.hand.map((card) => card.id));
      this.selectedCards.forEach((cardId) => {
        if (!validIds.has(cardId)) this.selectedCards.delete(cardId);
      });
    }
    this.roomCode = this.game.roomCode;

    // Detect my seat from players info
    if (this.mySeat === null && hand && hand.length > 0) {
      // Seat is assigned via ROOM_JOINED message, not here
    }
    this.notify();
  }

  setRoomJoin(code: string, seat: SeatIndex | null): void {
    const normalizedCode = code.trim().toUpperCase();
    const roomChanged = this.roomCode !== normalizedCode;
    const seatChanged = this.mySeat !== seat;
    if (!roomChanged && !seatChanged) return;
    this.roomCode = normalizedCode;
    this.mySeat = seat;
    this.notify();
  }

  setSeat(seat: SeatIndex | null): void {
    if (this.mySeat === seat) return;
    this.mySeat = seat;
    this.notify();
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const l of this.listeners) {
      try {
        l(this);
      } catch (e) {
        console.error("[state] Listener error:", e);
      }
    }
  }

  // Map absolute seat index to relative screen position
  relativePosition(
    seat: SeatIndex
  ): "self" | "left" | "across" | "right" {
    if (this.mySeat === null) return "across";
    const active = this.activeSeats();
    const myIndex = active.indexOf(this.mySeat);
    const seatIndex = active.indexOf(seat);
    if (myIndex === -1 || seatIndex === -1) {
      return "across";
    }
    const diff = (seatIndex - myIndex + active.length) % active.length;
    if (active.length === 3) {
      return (["self", "left", "right"] as const)[diff];
    }
    return (["self", "left", "across", "right"] as const)[diff];
  }

  // Get which absolute seat is at a given relative position
  seatAtPosition(pos: "self" | "left" | "across" | "right"): SeatIndex | null {
    if (this.mySeat === null) return null;
    const active = this.activeSeats();
    const myIndex = active.indexOf(this.mySeat);
    if (myIndex === -1) return null;
    if (active.length === 3) {
      const offsets: Record<string, number | null> = {
        self: 0,
        left: 1,
        across: null,
        right: 2,
      };
      const offset = offsets[pos];
      return offset === null ? null : active[(myIndex + offset) % active.length];
    }
    const offsets: Record<string, number> = {
      self: 0,
      left: 1,
      across: 2,
      right: 3,
    };
    return active[(myIndex + offsets[pos]) % active.length];
  }

  toggleCardSelection(cardId: string): void {
    if (this.selectedCards.has(cardId)) {
      this.selectedCards.delete(cardId);
    } else {
      // Enforce exchange max during exchange phase
      if (this.game?.phase === "exchange") {
        const max = this.getExchangeMax();
        if (max > 0 && this.selectedCards.size >= max) return;
      }
      this.selectedCards.add(cardId);
    }
    this.notify();
  }

  getExchangeLimits(): { min: number; max: number } {
    const g = this.game;
    if (!g || g.phase !== "exchange" || this.mySeat === null) return { min: 0, max: 0 };
    const isOmbre = this.mySeat === g.ombre;
    const contract = g.contract;
    const isSolo = contract === "solo" || contract === "solo_oros";
    const isOros = contract === "oros" || contract === "solo_oros";
    if (contract === "bola") return { min: 0, max: 0 };
    if (contract === "contrabola") return isOmbre ? { min: 1, max: 1 } : { min: 0, max: 0 };
    if (isOmbre) {
      if (isSolo) return { min: 0, max: 0 };
      if (contract === "volteo") return { min: 1, max: Math.min(8, this.hand.length, g.exchange.talonSize + 1) };
      return { min: 0, max: Math.min(isOros ? 6 : 8, g.exchange.talonSize) };
    }
    return { min: 0, max: Math.min(this.hand.length, g.exchange.talonSize) };
  }

  getExchangeMax(): number {
    return this.getExchangeLimits().max;
  }

  clearSelection(): void {
    if (this.selectedCards.size === 0) return;
    this.selectedCards.clear();
    this.notify();
  }

  get isMyTurn(): boolean {
    return (
      this.game !== null &&
      this.mySeat !== null &&
      this.game.turn === this.mySeat
    );
  }

  get canExchangeNow(): boolean {
    if (!this.game || this.mySeat === null) return false;
    if (this.game.phase !== "exchange") return false;
    if (this.game.exchange.completed.includes(this.mySeat)) return false;
    return (
      this.game.turn === this.mySeat &&
      this.game.exchange.order.includes(this.mySeat)
    );
  }

  get canDeferExchangeOrder(): boolean {
    if (!this.game || this.mySeat === null) return false;
    if (this.game.phase !== "exchange" || this.game.turn !== this.mySeat) return false;
    const contract = this.game.contract;
    if (
      !contract ||
      contract === "solo" ||
      contract === "solo_oros" ||
      contract === "contrabola" ||
      contract === "bola"
    ) {
      return false;
    }
    const ombre = this.game.ombre;
    if (ombre === null) return false;
    const completed = this.game.exchange.completed;
    if (completed.length !== 1 || !completed.includes(ombre)) return false;
    const pendingDefenders = this.game.exchange.order.filter(
      (s) => s !== ombre && !completed.includes(s)
    );
    return pendingDefenders.length === 2 && pendingDefenders[0] === this.mySeat;
  }

  get canCloseHandNow(): boolean {
    // The server computes this authoritatively and sends it as canCloseHand on GameState.
    return this.game?.canCloseHand ?? false;
  }

  get phase(): string {
    return this.game?.phase || "lobby";
  }

  reset(): void {
    this.game = null;
    this.hand = [];
    this.mySeat = null;
    this.selectedCards.clear();
    this.roomCode = null;
    this.notify();
  }
}
